/**
 * Cross-platform "save this file" primitive for exports (brief 01-export.md,
 * "Notes for All Modes").
 *
 * Confirmed on a physical iPad: the blob-URL `<a download>` trick this file
 * used to rely on exclusively is silently swallowed by WKWebView (the engine
 * Capacitor apps run in on iOS) — no error, no native download, no share
 * sheet, nothing the user can find. WKWebView doesn't implement the same
 * download-link handling Safari's own browser chrome has; this is a known gap,
 * not a bug in our click/anchor code. `<a download>` DOES work in an ordinary
 * web browser (dev server, desktop), so that path stays for web.
 *
 * On a native platform (`Capacitor.isNativePlatform()`), write the file into
 * the app's cache directory via @capacitor/filesystem, then hand it to
 * @capacitor/share, which opens the native share sheet — the user picks
 * Save to Files, Photos, AirDrop, Mail, etc. This is the documented fallback
 * the original brief anticipated if the blob-anchor approach proved
 * unreliable in practice.
 */
import { Capacitor } from '@capacitor/core';
import { Directory, Filesystem } from '@capacitor/filesystem';
import { Share } from '@capacitor/share';

function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = reader.result as string;
      // Filesystem.writeFile wants raw base64 — strip the "data:...;base64," prefix.
      const comma = result.indexOf(',');
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () => reject(reader.error ?? new Error('Failed to read blob'));
    reader.readAsDataURL(blob);
  });
}

async function saveNative(blob: Blob, filename: string): Promise<void> {
  const data = await blobToBase64(blob);
  const { uri } = await Filesystem.writeFile({ path: filename, data, directory: Directory.Cache });
  // `files` (not `url`) is the field meant for local file:// URIs — added
  // specifically for "files to be shared", vs. `url`'s primary use as a link.
  await Share.share({ files: [uri] });
}

function downloadWeb(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Revoke on a delay — some browsers need the navigation/download to start
  // reading the blob before the URL is torn down.
  setTimeout(() => URL.revokeObjectURL(url), 4000);
}

export function downloadBlob(blob: Blob, filename: string): void {
  if (!Capacitor.isNativePlatform()) {
    downloadWeb(blob, filename);
    return;
  }
  saveNative(blob, filename).catch((err: unknown) => {
    // The bug this replaces was a SILENT no-op — surface failures instead,
    // even crudely, rather than leaving the artist wondering where it went.
    console.error('Export failed to save/share:', err);
    alert(`Couldn't export "${filename}" — ${err instanceof Error ? err.message : String(err)}`);
  });
}

export function downloadCanvasAsPng(canvas: HTMLCanvasElement, filename: string): void {
  canvas.toBlob((blob) => {
    if (blob) downloadBlob(blob, filename);
  }, 'image/png');
}

export function downloadText(text: string, filename: string, mime = 'text/plain'): void {
  downloadBlob(new Blob([text], { type: mime }), filename);
}
